import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, forwardRef, ElementRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

declare var Quill: any;

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;
  
  @Input() placeholder: string = 'Type your text here...';
  @Input() height: string = '200px';
  @Input() readonly: boolean = false;
  @Output() contentChange = new EventEmitter<string>();

  private quill: any;
  private value: string = '';
  private onChange = (value: string) => {};
  private onTouched = () => {};

  ngOnInit(): void {
    // Delay initialization to ensure DOM is ready and Quill is loaded
    setTimeout(() => {
      this.initializeEditor();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.quill) {
      this.quill = null;
    }
  }

  private initializeEditor(): void {
    if (typeof Quill !== 'undefined' && this.editorContainer?.nativeElement) {
      try {
        this.quill = new Quill(this.editorContainer.nativeElement, {
          modules: {
            toolbar: [
              [{ 'header': [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              [{ 'color': [] }, { 'background': [] }],
              [{ 'align': [] }],
              ['link'],
              ['clean']
            ]
          },
          placeholder: this.placeholder,
          theme: 'snow',
          readOnly: this.readonly
        });

        // Set initial height
        const editor = this.editorContainer.nativeElement.querySelector('.ql-editor');
        if (editor) {
          editor.style.minHeight = this.height;
        }

        // Listen for text changes
        this.quill.on('text-change', () => {
          const content = this.quill.root.innerHTML;
          this.value = content === '<p><br></p>' ? '' : content;
          this.onChange(this.value);
          this.contentChange.emit(this.value);
        });

        // Listen for selection changes (for touched state)
        this.quill.on('selection-change', () => {
          this.onTouched();
        });

        // Set initial value if exists
        if (this.value) {
          this.quill.root.innerHTML = this.value;
        }
      } catch (error) {
        console.error('Error initializing Quill editor:', error);
      }
    } else {
      // Retry after a longer delay if Quill is not ready
      setTimeout(() => {
        this.initializeEditor();
      }, 500);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    if (this.quill) {
      this.quill.root.innerHTML = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.quill) {
      this.quill.enable(!isDisabled);
    }
  }

  // Public methods
  getContent(): string {
    return this.value;
  }

  setContent(content: string): void {
    this.writeValue(content);
    this.onChange(content);
  }

  focus(): void {
    if (this.quill) {
      this.quill.focus();
    }
  }
}
